import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, BadgePercent } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { loansImg } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const MIN = 500000;
const MAX = 100000000;

const formatAmount = (v) => {
  if (v >= MAX) return "$100M+";
  if (v >= 1000000) {
    const m = v / 1000000;
    return `$${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  return `$${Math.round(v / 1000)}K`;
};

export default function Loans() {
  const { t } = useLang();
  const [amount, setAmount] = useState(5000000);
  const features = ["loans.f1", "loans.f2", "loans.f3", "loans.f4"];
  const fillPct = ((amount - MIN) / (MAX - MIN)) * 100;

  return (
    <section id="loans" className="relative py-24 lg:py-32 overflow-hidden">
      <div
        className="aurora-blob w-[500px] h-[500px] top-20 -right-40"
        style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 65%)", animationDelay: "-4s" }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute -inset-3 rounded-[2.2rem] bg-gradient-to-br from-gold/25 via-transparent to-violet-500/15 blur-xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-gold/30 shadow-2xl shadow-black/15">
              <img src={loansImg} alt="Executive boardroom" loading="lazy" className="w-full h-[380px] lg:h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-transparent to-transparent" />
            </div>
            <div className="animate-floaty absolute -bottom-6 left-6 glass rounded-2xl px-6 py-4 flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-gold-deep shrink-0">
                <BadgePercent size={20} strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-display text-xl text-gold-deep">$500K – $100M+</p>
                <p className="text-xs text-slate-600">{t("loans.eyebrow")}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7 lg:pl-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="flex items-center gap-3 font-mono-brand text-xs tracking-[0.28em] uppercase text-gold-deep mb-5">
              <span className="h-px w-10 bg-gold/70" />
              {t("loans.eyebrow")}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-navy-900 max-w-xl">
              {t("loans.title")}
            </h2>
            <p className="mt-5 max-w-xl text-slate-600 leading-relaxed">{t("loans.desc")}</p>

            <div className="mt-9 grid sm:grid-cols-2 gap-4 max-w-xl">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl card-navy px-5 py-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold-deep shrink-0">
                    <Check size={15} strokeWidth={2.25} />
                  </span>
                  <span className="text-sm text-navy-800">{t(f)}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 max-w-xl card-navy rounded-3xl p-7 border-gold/25">
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-slate-600">{t("loans.slider")}</p>
                <p data-testid="loan-amount-display" className="font-display text-2xl sm:text-3xl text-gold-deep">
                  {formatAmount(amount)}
                </p>
              </div>
              <input
                data-testid="loan-estimator-slider"
                type="range"
                min={MIN}
                max={MAX}
                step={500000}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="range-gold"
                style={{ "--fill": `${fillPct}%` }}
                aria-label={t("loans.slider")}
              />
              <div className="mt-3 flex justify-between text-xs text-slate-500 font-mono-brand">
                <span>$500K</span>
                <span>$10M</span>
                <span>$50M</span>
                <span>$100M+</span>
              </div>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-5">
              <button
                data-testid="loans-cta-button"
                onClick={() => scrollToId("#contact")}
                className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold"
              >
                {t("loans.cta")}
                <ArrowRight size={17} strokeWidth={2} />
              </button>
              <p className="font-mono-brand text-xs text-slate-500 tracking-wide">{t("loans.steps")}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
